import { useState, useEffect, useCallback } from 'react';
import { supabaseService } from '../utils/supabaseService';

export const usePartnerData = () => {
  const [metrics, setMetrics] = useState({
    totalPartners: 0,
    activeCampaigns: 0,
    totalCommissions: 0,
    monthlyGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await supabaseService?.getPartnerMetrics();
      setMetrics(data);
    } catch (error) {
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics
  };
};

export const useTopPerformers = () => {
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPerformers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await supabaseService?.getTopPerformers();
      setPerformers(data);
    } catch (error) {
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPerformers();
  }, [fetchPerformers]);

  return {
    performers,
    loading,
    error,
    refetch: fetchPerformers
  };
};

export const usePendingApprovals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await supabaseService?.getPendingApprovals();
      setApprovals(data);
    } catch (error) {
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const approvePartner = useCallback(async (partnerId) => {
    try {
      await supabaseService?.update('partners', partnerId, { status: 'active' });
      await fetchApprovals(); // Refresh list
    } catch (error) {
      throw new Error(`Failed to approve partner: ${error.message}`);
    }
  }, [fetchApprovals]);

  const rejectPartner = useCallback(async (partnerId) => {
    try {
      await supabaseService?.update('partners', partnerId, { status: 'rejected' });
      await fetchApprovals(); // Refresh list
    } catch (error) {
      throw new Error(`Failed to reject partner: ${error.message}`);
    }
  }, [fetchApprovals]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  return {
    approvals,
    loading,
    error,
    approvePartner,
    rejectPartner,
    refetch: fetchApprovals
  };
};